import React, { useState, useMemo, useRef } from "react";
import { calculateTotal, aggregateTree } from "../../services/costs/calculations.js";

const formatCHF = (v) =>
  (v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, "'");

const UNIT_LABELS = {
  pl: "pauschal",
  st: "Stück",
  m: "Laufmeter",
  m2: "m²",
  m3: "m³",
  h: "Stunden",
};

const ExcelImportPreview = ({
  tree: initialTree,
  meta,
  customColumns,
  mismatches: initialMismatches = {},
  existingCustomColumns = [],
  projects = [],
  defaultPhases = [],
  onImport,
  onCancel,
}) => {
  const [tree, setTree] = useState(() => ({ ...initialTree }));
  // Store Excel totals once (they never change)
  const [excelTotals] = useState(() => {
    const totals = {};
    for (const [code, m] of Object.entries(initialMismatches)) {
      totals[code] = m.excelTotal;
    }
    return totals;
  });

  // Recompute mismatches whenever the tree changes
  const mismatches = useMemo(() => {
    const result = {};
    for (const [code, excelTotal] of Object.entries(excelTotals)) {
      const node = tree[code];
      if (!node) continue;
      const calcTotal = node.totalComputed || 0;
      if (Math.abs(excelTotal - calcTotal) > 0.5) {
        result[code] = { excelTotal, calcTotal };
      }
    }
    return result;
  }, [tree, excelTotals]);
  const [editingCell, setEditingCell] = useState(null);
  const [localValue, setLocalValue] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const inputRef = useRef(null);

  // Form state
  const [projectCode, setProjectCode] = useState(meta.project || "");
  const [phase, setPhase] = useState(() => {
    // Phase-Praefix strikt vergleichen, damit "3" nicht versehentlich "31 - ..." matcht.
    // Erlaubt sind nur genaue Praefixe wie "31" -> "31 - Vorprojekt".
    if (meta.phase) {
      const metaPrefix = meta.phase.trim().split(/\s+/)[0];
      const match = defaultPhases.find((p) => p.split(/\s+/)[0] === metaPrefix);
      if (match) return match;
    }
    return defaultPhases[0] || "";
  });
  const [dossierName, setDossierName] = useState("");

  // Custom column mapping: for each imported custom column, decide action
  // "import_new" = create new column, "map_to:<id>" = map to existing, "skip" = ignore
  const [columnActions, setColumnActions] = useState(() => {
    const actions = {};
    for (const col of customColumns) {
      // Try auto-matching to existing columns by name
      const match = existingCustomColumns.find(
        (ec) => ec.name.toLowerCase() === col.headerName.toLowerCase()
      );
      actions[col.headerName] = match ? `map_to:${match.id}` : "import_new";
    }
    return actions;
  });

  // Sorted root codes
  const rootCodes = useMemo(() => {
    return Object.values(tree)
      .filter((n) => n.level === 1)
      .sort((a, b) => parseInt(a.code, 10) - parseInt(b.code, 10))
      .map((n) => n.code);
  }, [tree]);

  // Flatten visible rows
  const visibleRows = useMemo(() => {
    const rows = [];
    const walk = (code, depth) => {
      const node = tree[code];
      if (!node) return;
      rows.push({ node, depth });
      const children = [...(node.children || [])].sort((a, b) => {
        const na = tree[a], nb = tree[b];
        if (!na || !nb) return 0;
        const codeA = a.includes(".") ? parseFloat(a) : parseInt(a, 10);
        const codeB = b.includes(".") ? parseFloat(b) : parseInt(b, 10);
        return codeA - codeB;
      });
      for (const child of children) walk(child, depth + 1);
    };
    for (const rc of rootCodes) walk(rc, 0);
    return rows;
  }, [tree, rootCodes]);

  const projectTotal = useMemo(() => {
    return Object.values(tree)
      .filter((n) => n.active)
      .reduce((sum, n) => {
        // Only count leaves (no active children)
        const hasActiveChild = (n.children || []).some((c) => tree[c]?.active);
        if (hasActiveChild) return sum;
        return sum + (n.totalComputed || 0);
      }, 0);
  }, [tree]);

  // Focus input on edit
  React.useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editingCell]);

  const startEdit = (code, field) => {
    const node = tree[code];
    setEditingCell({ code, field });
    setLocalValue(String(node[field] ?? ""));
  };

  const commitEdit = (code, field) => {
    setTree((prev) => {
      const node = { ...prev[code] };
      if (["length", "width", "height", "quantity", "unitPrice", "factor"].includes(field)) {
        node[field] = parseFloat(localValue) || 0;
      } else {
        node[field] = localValue;
      }
      node.totalComputed = calculateTotal(node);
      const updated = { ...prev, [code]: node };
      // Re-aggregate parent totals
      return aggregateTree(updated);
    });
    setEditingCell(null);
    setLocalValue("");
  };

  const toggleActive = (code) => {
    setTree((prev) => {
      const updated = { ...prev, [code]: { ...prev[code], active: !prev[code].active } };
      return aggregateTree(updated);
    });
  };

  const handleKeyDown = (e, code, field) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit(code, field);
    } else if (e.key === "Escape") {
      setEditingCell(null);
      setLocalValue("");
    }
  };

  const handleContextMenu = (e, node) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, node });
  };

  // Close context menu on click
  React.useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [contextMenu]);

  const addSibling = (siblingNode) => {
    const parentCode = siblingNode.parentCode;
    if (!parentCode) return;

    // Find next available sibling code
    const parent = tree[parentCode];
    if (!parent) return;

    const siblings = (parent.children || []).filter((c) => tree[c]);
    let newCode;

    if (siblingNode.code.includes(".")) {
      // Level 4/5: e.g. siblings are 113.1, 113.2 -> next is 113.3
      const parts = siblingNode.code.split(".");
      const prefix = parts[0];
      const afterDot = parts[1];

      if (afterDot.length === 1) {
        // Level 4 sibling
        const nums = siblings.map((c) => {
          const p = c.split(".");
          return p.length === 2 && p[1].length === 1 ? parseInt(p[1], 10) : 0;
        }).filter((n) => !isNaN(n));
        const next = (nums.length > 0 ? Math.max(...nums) : 0) + 1;
        newCode = `${prefix}.${next}`;
      } else {
        // Level 5 sibling
        const parentAfterDot = parentCode.split(".")[1] || "";
        const nums = siblings.map((c) => {
          const p = c.split(".");
          if (p.length !== 2 || !p[1].startsWith(parentAfterDot)) return -1;
          return parseInt(p[1].substring(parentAfterDot.length), 10);
        }).filter((n) => n >= 0);
        const next = (nums.length > 0 ? Math.max(...nums) : 0) + 1;
        newCode = `${prefix}.${parentAfterDot}${next}`;
      }
    } else {
      // Level 2/3: e.g. siblings are 131, 132 -> next is 137
      const codeLen = siblingNode.code.length;
      const nums = siblings.map((c) => c.length === codeLen ? parseInt(c, 10) : 0).filter((n) => n > 0);
      const next = (nums.length > 0 ? Math.max(...nums) : parseInt(parentCode + "0", 10)) + 1;
      newCode = String(next);
    }

    const newNode = {
      code: newCode,
      name: "",
      source: "",
      length: 0,
      width: 0,
      height: 0,
      quantity: 0,
      unit: "pl",
      unitPrice: 0,
      factor: 1,
      totalComputed: 0,
      active: true,
      level: siblingNode.level,
      parentCode,
      children: [],
      tags: [],
      customFields: {},
      phase: "default",
      isUserAdded: true,
    };

    setTree((prev) => {
      const updated = { ...prev, [newCode]: newNode };
      // Add to parent's children
      const parentNode = { ...updated[parentCode] };
      parentNode.children = [...(parentNode.children || []), newCode];
      updated[parentCode] = parentNode;
      return aggregateTree(updated);
    });

    setContextMenu(null);
    // Start editing the name of the new node
    setTimeout(() => startEdit(newCode, "name"), 50);
  };

  const handleImport = () => {
    // Build custom column definitions and values to pass to the import handler
    const columnsToImport = [];
    for (const col of customColumns) {
      const action = columnActions[col.headerName];
      if (action === "skip") continue;
      columnsToImport.push({
        headerName: col.headerName,
        action,
        values: col.values,
      });
    }

    onImport({
      tree,
      projectCode,
      phase,
      dossierName: dossierName.trim() || phase,
      meta,
      customColumnsToImport: columnsToImport,
    });
  };

  const canImport = projectCode && phase;

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal-content import-preview-modal">
        <div className="modal-header">
          <h3>Excel-Import Vorschau</h3>
          <button className="modal-close" onClick={onCancel}>&times;</button>
        </div>

        <div className="modal-body">
          {/* Metadata form */}
          <div className="import-meta-form">
            <div className="import-meta-row">
              <div className="form-group">
                <label>Projekt *</label>
                <select value={projectCode} onChange={(e) => setProjectCode(e.target.value)}>
                  <option value="">-- Projekt wählen --</option>
                  {projects.map((p) => (
                    <option key={p.code} value={p.code}>{p.code} - {p.title}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Phase *</label>
                <select value={phase} onChange={(e) => setPhase(e.target.value)}>
                  <option value="">-- Phase wählen --</option>
                  {defaultPhases.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={dossierName}
                  onChange={(e) => setDossierName(e.target.value)}
                  placeholder={phase || "Dossiername..."}
                />
              </div>
            </div>

            {meta.accuracy && (
              <div className="import-meta-info">
                Genauigkeit: {meta.accuracy}
                {meta.author && <> | Verfasser: {meta.author}</>}
                {meta.date && <> | Datum: {meta.date}</>}
              </div>
            )}
          </div>

          {/* Custom columns mapping */}
          {customColumns.length > 0 && (
            <div className="import-custom-columns">
              <h4>Zusätzliche Spalten</h4>
              <div className="import-col-mapping-list">
                {customColumns.map((col) => {
                  const sampleValues = Object.values(col.values).slice(0, 5).join(", ");
                  return (
                    <div key={col.headerName} className="import-col-mapping-row">
                      <div className="import-col-name">
                        <strong>{col.headerName}</strong>
                        <span className="import-col-sample">z.B. {sampleValues || "–"}</span>
                      </div>
                      <select
                        value={columnActions[col.headerName]}
                        onChange={(e) => setColumnActions((prev) => ({ ...prev, [col.headerName]: e.target.value }))}
                      >
                        <option value="import_new">Neue Spalte erstellen</option>
                        <option value="add_to_tags">Zu Tags hinzufügen</option>
                        {existingCustomColumns.map((ec) => (
                          <option key={ec.id} value={`map_to:${ec.id}`}>
                            Zuordnen zu: {ec.name}
                          </option>
                        ))}
                        <option value="skip">Nicht importieren</option>
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Data preview table */}
          <div className="import-preview-info">
            <span>
              {Object.keys(tree).length} Positionen | Total: CHF {formatCHF(projectTotal)}
              {Object.keys(mismatches).length > 0 && (
                <span className="mismatch-summary">
                  {" "}| {Object.keys(mismatches).length} Abweichung{Object.keys(mismatches).length !== 1 ? "en" : ""} zum Excel-Total
                </span>
              )}
            </span>
            <span className="import-preview-hint">Klicken Sie auf Werte um sie zu bearbeiten</span>
          </div>

          <div className="import-table-container">
            <table className="bkp-table import-preview-table">
              <thead>
                <tr>
                  <th className="import-active-col"></th>
                  <th className="code-col">BKP</th>
                  <th className="name-col">Name</th>
                  <th className="source-col">Quelle</th>
                  <th className="dim-col">Länge</th>
                  <th className="dim-col">Breite</th>
                  <th className="dim-col">Höhe</th>
                  <th className="qty-col">Menge</th>
                  <th className="unit-col">Einheit</th>
                  <th className="price-col">EP</th>
                  <th className="factor-col">Faktor</th>
                  <th className="total-col">Total</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(({ node, depth }) => {
                  const isLeaf = !node.children || node.children.length === 0;
                  return (
                    <tr
                      key={node.code}
                      className={`bkp-row level-${node.level} ${!node.active ? "inactive-row" : ""}`}
                      onContextMenu={(e) => handleContextMenu(e, node)}
                    >
                      <td className="import-active-cell">
                        <input
                          type="checkbox"
                          checked={node.active}
                          onChange={() => toggleActive(node.code)}
                          title={node.active ? "Deaktivieren" : "Aktivieren"}
                        />
                      </td>
                      <td className="code-cell" style={{ paddingLeft: `${depth * 16 + 4}px` }}>
                        {node.code}
                      </td>
                      {renderEditableCell(node, "name", "text")}
                      {renderEditableCell(node, "source", "text")}
                      {renderEditableCell(node, "length", "number")}
                      {renderEditableCell(node, "width", "number")}
                      {renderEditableCell(node, "height", "number")}
                      {renderEditableCell(node, "quantity", "number")}
                      <td
                        className="unit-cell editable"
                        onClick={() => startEdit(node.code, "unit")}
                      >
                        {editingCell?.code === node.code && editingCell?.field === "unit" ? (
                          <select
                            ref={inputRef}
                            value={localValue}
                            onChange={(e) => {
                              setLocalValue(e.target.value);
                              setTree((prev) => ({
                                ...prev,
                                [node.code]: { ...prev[node.code], unit: e.target.value },
                              }));
                              setEditingCell(null);
                            }}
                            onBlur={() => setEditingCell(null)}
                          >
                            {Object.entries(UNIT_LABELS).map(([val, label]) => (
                              <option key={val} value={val}>{label}</option>
                            ))}
                          </select>
                        ) : (
                          UNIT_LABELS[node.unit] || node.unit
                        )}
                      </td>
                      {renderEditableCell(node, "unitPrice", "number")}
                      {renderEditableCell(node, "factor", "number")}
                      <td className={`total-cell ${!node.active ? "inactive" : ""} ${mismatches[node.code] ? "mismatch" : ""}`}
                        title={mismatches[node.code] ? `Excel: CHF ${formatCHF(mismatches[node.code].excelTotal)} | Berechnet: CHF ${formatCHF(mismatches[node.code].calcTotal)}` : undefined}
                      >
                        {formatCHF(node.totalComputed)}
                        {mismatches[node.code] && (
                          <span className="mismatch-icon" title={`Excel-Total abweichend: CHF ${formatCHF(mismatches[node.code].excelTotal)}`}>!</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Context Menu */}
          {contextMenu && (
            <div
              className="import-context-menu"
              style={{ top: contextMenu.y, left: contextMenu.x }}
            >
              <div className="context-menu-header">{contextMenu.node.code} {contextMenu.node.name}</div>
              {contextMenu.node.parentCode && (
                <button
                  className="context-menu-item"
                  onClick={() => addSibling(contextMenu.node)}
                >
                  Nebenposition hinzufügen
                </button>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>Abbrechen</button>
          <button className="btn btn-primary" onClick={handleImport} disabled={!canImport}>
            Dossier erstellen & importieren
          </button>
        </div>
      </div>
    </div>
  );

  function renderEditableCell(node, field, type) {
    const isEditing = editingCell?.code === node.code && editingCell?.field === field;
    const value = node[field];
    let display;
    if (type === "number") {
      if (field === "unitPrice") display = value ? `CHF ${formatCHF(value)}` : "0";
      else if (field === "factor") display = (value || 1).toFixed(2);
      else display = value || 0;
    } else {
      display = value || "";
    }

    return (
      <td
        className={`${field}-cell ${type === "number" ? "number-cell" : ""} editable`}
        onClick={() => startEdit(node.code, field)}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            type={type === "number" ? "number" : "text"}
            step={type === "number" ? "0.01" : undefined}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() => commitEdit(node.code, field)}
            onKeyDown={(e) => handleKeyDown(e, node.code, field)}
          />
        ) : (
          display
        )}
      </td>
    );
  }
};

export default ExcelImportPreview;
